import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";
import {
  AddBundleRequest,
  ContractBundle,
  useAddBundleToContract,
  useAddBundleToDeal
} from "@/pages/contracts/hooks";
import { useProductBundles } from "@/pages/settings/pages/products-v2/hooks";
import {
  ProductBundle,
  ServiceItem,
  ServiceItemType
} from "@/pages/settings/pages/products-v2/types";
import { useFeatureFlag } from "@/services/features/hook";
import { FeatureFlags } from "@/services/features/types";
import { bundleServiceOptions, isBundleDiscountRefused } from "../bundleLines";
import { DealBundleDrawer } from "../components/DealBundleDrawer";

interface UseAddBundleArgs {
  // Only the deal's Terms & Services offers bundles.
  offerBundles: boolean;
  productId?: string;
  // The deal's contract; without one the add creates it.
  contractId?: string;
  dealId?: string;
  services: ServiceItem[];
  serviceItemTypes: ServiceItemType[];
  currency?: string | null;
  disabled?: boolean;
  onAdded?: (bundle: ContractBundle) => void;
}

// Bundles for Add service, and the drawer that adds the one picked.
export const useAddBundle = ({
  offerBundles,
  productId,
  contractId,
  dealId,
  services,
  serviceItemTypes,
  currency,
  disabled = false,
  onAdded
}: UseAddBundleArgs) => {
  const { t } = useTranslation();
  const { enabled: isFlagOn } = useFeatureFlag(
    FeatureFlags.CRM_11089_PRODUCT_BUNDLES
  );
  const enabled = offerBundles && isFlagOn && !!productId;
  const { data } = useProductBundles(productId ?? "", { enabled });
  // Each bundle keeps only the services the deal can take, so the menu's count
  // and preview match the drawer; a bundle left with none isn't offered.
  const bundles = useMemo(
    () =>
      enabled
        ? (data?.bundles ?? []).flatMap((bundle) => {
            const items = bundleServiceOptions(bundle, services).map(
              ({ item }) => item
            );
            return items.length > 0 ? [{ ...bundle, items }] : [];
          })
        : [],
    [enabled, data, services]
  );

  const addToContract = useAddBundleToContract();
  const addToDeal = useAddBundleToDeal();
  const isSaving = addToContract.isPending || addToDeal.isPending;
  const [pickedBundle, setPickedBundle] = useState<ProductBundle | null>(null);

  useEffect(() => {
    if (disabled) setPickedBundle(null);
  }, [disabled]);

  const save = (request: AddBundleRequest, onDiscountRefused: () => void) => {
    const callbacks = {
      onSuccess: (bundle: ContractBundle) => {
        setPickedBundle(null);
        onAdded?.(bundle);
      },
      onError: (error: unknown) =>
        isBundleDiscountRefused(error)
          ? onDiscountRefused()
          : toast.error(t("contractTerms.bundle.saveError"))
    };
    if (contractId) {
      addToContract.mutate(
        { pathParams: { contractId }, data: request },
        callbacks
      );
    } else if (dealId) {
      addToDeal.mutate({ pathParams: { dealId }, data: request }, callbacks);
    }
  };

  const drawer = pickedBundle ? (
    <DealBundleDrawer
      key={pickedBundle.id}
      bundle={pickedBundle}
      services={services}
      serviceItemTypes={serviceItemTypes}
      currency={currency}
      isSaving={isSaving}
      onClose={() => setPickedBundle(null)}
      onSave={save}
    />
  ) : null;

  return {
    bundles,
    // Undefined when nothing can be added, so the menu shows no chips.
    onSelectBundle:
      bundles.length > 0 && (contractId || dealId)
        ? (bundle: ProductBundle) => {
            if (!disabled) setPickedBundle(bundle);
          }
        : undefined,
    drawer
  };
};
